import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Anchor, ChevronRight, Dices, Flame, Gift, LayoutGrid,
  PlayCircle, Rocket, Sparkles, Trophy,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../../lib/api';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { useSettings } from '../../context/SettingsContext';
import { useHiddenCategories } from '../../lib/gameCategories';
import { cacheSliders, readCachedSliders } from '../../lib/sliderCache';
import { slidersAt } from '../../lib/sliderPlacement';
import { brandName } from '../../lib/brand';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import AuthModal from '../../components/AuthModal';
import GameArtwork from '../../components/GameArtwork';
import { providersFromResponse } from '../../components/ProviderStrip';
import HerkulSection from './HerkulSection';
import HerkulGameCard from './HerkulGameCard';
import HerkulLiveBets from './HerkulLiveBets';
import HerkulFaq from './HerkulFaq';
import { SECTIONS, sectionHook } from '../../lib/domHooks';
import HomeBlocks, { HomeBlock } from '../../components/HomeBlocks';

const STRIP_LIMIT = 14;
const FEATURE_LIMIT = 12;

const mapGame = (game) => ({
  id: game.id,
  name: game.name,
  provider: game.provider,
  img: game.image,
  hot: game.hot,
  new: game.new,
  api_provider: game.api_provider,
  game_id: game.game_id,
  demo_supported: !!game.demo_supported,
});

const HerkulHome = () => {
  const settings = useSettings();
  const gl = useGameLauncher();

  const [banners, setBanners] = useState(readCachedSliders);
  const [wins, setWins] = useState([]);
  const [popular, setPopular] = useState({ items: [], total: 0 });
  const [live, setLive] = useState({ items: [], total: 0 });
  const [fresh, setFresh] = useState([]);
  const [crash, setCrash] = useState({ items: [], total: 0 });
  const [providers, setProviders] = useState([]);
  const [featured, setFeatured] = useState(null);
  const hiddenCats = useHiddenCategories();

  useEffect(() => {
    const get = (url, params) => client.get(url, { params }).then(({ data }) => data).catch(() => null);
    const asList = (data) => ({ items: data?.items || [], total: Number(data?.total) || 0 });

    get('/sliders').then((data) => {
      if (!data) return;
      cacheSliders(data.items || []);
      setBanners(data.items || []);
    });
    // Son Kazananlar şeridi — gerçek kazançlı turlar (maskeli kullanıcı adı).
    get('/games/recent-wins', { limit: 14 }).then((data) => setWins(data?.items || []));
    get('/games/showcase', { strip: 'popular', category: 'slot', limit: FEATURE_LIMIT })
      .then((data) => setPopular(asList(data)));
    get('/games/showcase', { strip: 'new', category: 'slot', limit: STRIP_LIMIT })
      .then((data) => setFresh(data?.items || []));
    get('/games', { category: 'live-casino', limit: STRIP_LIMIT }).then((data) => setLive(asList(data)));
    get('/games', { category: 'crash', limit: STRIP_LIMIT }).then((data) => setCrash(asList(data)));
    // Referansta bu slotta bir turnuva kartı var; platformda turnuva
    // yok (08.08.2026'da bilinçli kaldırıldı). Slot uydurma rakamla
    // doldurulmaz — yerine gerçek bir kampanya çizilir.
    get('/bonuses').then((data) => {
      const list = Array.isArray(data) ? data : (data?.items || []);
      setFeatured(list.find((item) => item?.title) || null);
    });
    get('/game-providers', { category: 'slot' }).then((data) => {
      setProviders(providersFromResponse(data));
    });
    // Popüler şeridin "Tümünü Gör (N)" sayısı için slot toplamı.
    get('/games', { category: 'slot', limit: 1 }).then((data) => {
      setPopular((prev) => ({ ...prev, total: Number(data?.total) || prev.total }));
    });
  }, []);

  const liveOpen = !hiddenCats.includes('live-casino');
  const sportsOpen = settings.sports_enabled !== false;

  // Üstteki üç büyük hero kartı — referans arayüzün imzası (Casino / Ödüllü
  // Çark / Spor Bahisleri) her zaman çizilir; kart görseli panelden yüklenen
  // ilk bannerlardan alınır, yoksa gradyan zemin kalır. Spor kartı yalnızca
  // spor bölümü tümüyle kapalıysa düşer.
  // Kart görseli yalnızca ana slider banner'larından alınır; yan banner ve
  // katalog sayfası banner'ları ana sayfanın hero kartlarına düşmemelidir.
  const bannerArt = slidersAt(banners).filter((b) => b.image).map((b) => b.image);
  const heroCards = [
    {
      key: 'casino', to: '/slotlar', title: 'SLOT SALONU', tag: 'Casino',
      icon: Dices, art: bannerArt[0], from: '#0a5f4f', to2: '#02201d',
    },
    {
      key: 'cark', to: '/cark', title: 'ÖDÜLLÜ ÇARK', tag: 'Her gün ücretsiz',
      icon: Anchor, art: bannerArt[1], from: '#7a5a12', to2: '#241a04',
    },
    sportsOpen && {
      key: 'spor', to: '/spor', title: 'SPOR BAHİSLERİ', tag: 'Spor',
      icon: Trophy, art: bannerArt[2], from: '#0a4a5f', to2: '#021a20',
    },
  ].filter(Boolean);

  return (
    <>
      <HomeBlocks theme="herkul">
        <HomeBlock id={SECTIONS.HERO}>
        {heroCards.length > 0 && (
          <div className="herkul-hero-cards" {...sectionHook(SECTIONS.HERO)}>
            {heroCards.map((card) => (
              <Link
                key={card.key}
                to={card.to}
                className="herkul-hero-card"
                style={{ '--hc-from': card.from, '--hc-to': card.to2 }}
              >
                {card.art && (
                  <img
                    className="herkul-hero-art"
                    src={resolveApiAssetUrl(card.art)}
                    alt=""
                    aria-hidden="true"
                  />
                )}
                <span className="herkul-hero-overlay" />
                <span className="herkul-hero-copy">
                  <span className="herkul-hero-title">{card.title}</span>
                  <span className="herkul-hero-cta">
                    <card.icon size={15} />
                    {card.tag}
                    <ChevronRight size={15} />
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.WINS}>
        {wins.length > 0 && (
          <section className="herkul-wins" {...sectionHook(SECTIONS.WINS)}>
            <h2 className="herkul-wins-title">Son Kazananlar</h2>
            <div className="herkul-wins-strip">
              {wins.map((win, index) => (
                <div className="herkul-win" key={`${win.user}-${index}`}>
                  <span className="herkul-win-art">
                    <GameArtwork src={win.image} alt={win.game_name} />
                  </span>
                  <span className="herkul-win-user">{win.user}</span>
                  <span className="herkul-win-amount">
                    {Number(win.win).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                    {' ₺'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.POPULAR}>
        {popular.items.length > 0 && (
          <HerkulSection
            section={SECTIONS.POPULAR}
            title="En Popüler Oyunlar"
            subtitle="Türkiye'de en çok oynananlar"
            to="/slotlar"
            icon={Flame}
            tone="gold"
            count={popular.total}
            shown={popular.items.length}
          >
            {popular.items.map((game) => (
              <HerkulGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </HerkulSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.BONUSES}>
        <Link to="/promosyonlar" className="herkul-bonus-banner" {...sectionHook(SECTIONS.BONUSES)}>
          {bannerArt[3] && (
            <img
              className="herkul-bonus-art"
              src={resolveApiAssetUrl(bannerArt[3])}
              alt=""
              aria-hidden="true"
            />
          )}
          <span className="herkul-bonus-overlay" />
          <span className="herkul-bonus-copy">
            <span className="herkul-bonus-title">
              {brandName(settings) ? `${brandName(settings)}'de Bonusunu Kap` : 'Bonusunu Kap'}
            </span>
            <span className="herkul-bonus-sub">Fırsatı kaçırma, hemen katıl!</span>
          </span>
          <span className="herkul-bonus-cta">
            <Gift size={16} />
            Bonus Kap
          </span>
        </Link>
        </HomeBlock>

        <HomeBlock id={SECTIONS.LIVE_CASINO}>
        {liveOpen && live.items.length > 0 && (
          <HerkulSection
            section={SECTIONS.LIVE_CASINO}
            title="Canlı Casino"
            subtitle="Gerçek krupiyerle canlı masalar"
            to="/canli-casino"
            icon={PlayCircle}
            tone="red"
            count={live.total}
            shown={live.items.length}
          >
            {live.items.map((game) => (
              <HerkulGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </HerkulSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.NEW}>
        {fresh.length > 0 && (
          <HerkulSection
            section={SECTIONS.NEW}
            title="Yeni Oyunlar"
            subtitle="Katalogdaki en taze başlıklar"
            to="/slotlar"
            icon={Sparkles}
            tone="green"
          >
            {fresh.map((game) => (
              <HerkulGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </HerkulSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.FAST}>
        {crash.items.length > 0 && (
          <HerkulSection
            section={SECTIONS.FAST}
            title="Hızlı Oyunlar"
            subtitle="Saniyeler içinde sonuçlanan turlar"
            to="/crash"
            icon={Rocket}
            tone="blue"
            count={crash.total}
            shown={crash.items.length}
          >
            {crash.items.map((game) => (
              <HerkulGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </HerkulSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.PROVIDERS}>
        {providers.length > 0 && (
          <HerkulSection
            section={SECTIONS.PROVIDERS}
            title="Oyun Sağlayıcıları"
            subtitle="Katalogdaki üreticiler"
            to="/slotlar"
            icon={LayoutGrid}
            tone="purple"
          >
            {providers.map((provider) => (
              <Link
                key={provider.slug}
                to={`/slotlar?provider=${encodeURIComponent(provider.slug)}`}
                className="herkul-provider"
                title={provider.name}
              >
                {provider.logo ? (
                  <img src={resolveApiAssetUrl(provider.logo)} alt={provider.name} loading="lazy" />
                ) : provider.name}
              </Link>
            ))}
          </HerkulSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.FEATURED}>
        {featured && (
          <section className="herkul-featured" {...sectionHook(SECTIONS.FEATURED)}>
            <div className="herkul-section-head">
              <div className="herkul-section-title-wrap">
                <span className="herkul-section-icon is-red"><Trophy size={17} /></span>
                <div className="herkul-section-heading">
                  <h2 className="herkul-section-title">Öne Çıkan Kampanya</h2>
                  <p className="herkul-section-sub">Katıl, ödülü kap</p>
                </div>
              </div>
            </div>

            <Link to="/promosyonlar" className="herkul-featured-card">
              {featured.image && (
                <img
                  className="herkul-featured-art"
                  src={resolveApiAssetUrl(featured.image)}
                  alt=""
                  aria-hidden="true"
                />
              )}
              <span className="herkul-featured-overlay" />
              <span className="herkul-featured-body">
                {featured.badge && (
                  <span className="herkul-featured-badge">{featured.badge}</span>
                )}
                {/* Başlıktaki büyük rakam kampanyanın **gerçek** üst sınırıdır;
                    tanımlı değilse hiç çizilmez — uydurma bir ödül havuzu
                    göstermek yerine boş bırakmak doğrudur. */}
                {Number(featured.max_bonus) > 0 && (
                  <span className="herkul-featured-value">
                    {Number(featured.max_bonus).toLocaleString('tr-TR', {
                      maximumFractionDigits: 0,
                    })}
                    <small>₺'ye kadar</small>
                  </span>
                )}
                <span className="herkul-featured-title">{featured.title}</span>
                <span className="herkul-featured-stats">
                  {Number(featured.min_deposit) > 0 && (
                    <span>
                      Min. Yatırım
                      {' '}
                      <b>
                        {Number(featured.min_deposit).toLocaleString('tr-TR', {
                          maximumFractionDigits: 0,
                        })}
                        {' ₺'}
                      </b>
                    </span>
                  )}
                  {Number(featured.wager_multiplier) > 0 && (
                    <span>Çevrim <b>{featured.wager_multiplier}x</b></span>
                  )}
                </span>
              </span>
              <span className="herkul-featured-cta">
                Katıl
                <ChevronRight size={15} />
              </span>
            </Link>
          </section>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.LIVE_BETS}>
        <HerkulLiveBets />
        </HomeBlock>

        <HomeBlock id={SECTIONS.FAQ}>
        <HerkulFaq />
        </HomeBlock>
      </HomeBlocks>

      <GameChoiceModal
        game={gl.choice}
        user={gl.user}
        balance={gl.balance}
        bonusBalance={gl.bonusBalance}
        demoAvailable={gl.demoAvailable}
        onClose={gl.closeChoice}
        onReal={gl.startReal}
        onDemo={gl.startDemo}
        onLogin={() => gl.openAuth('login')}
        onRegister={() => gl.openAuth('register')}
      />
      {gl.activeGame && (
        <GameModal
          key={`${gl.activeGame.id}-${gl.mode}`}
          game={gl.activeGame}
          mode={gl.mode}
          onClose={gl.closeGame}
        />
      )}
      <AuthModal
        open={!!gl.authTab}
        onClose={() => gl.setAuthTab(null)}
        defaultTab={gl.authTab || 'login'}
      />
    </>
  );
};

export default HerkulHome;
